import React, { useState, useEffect } from 'react'
import useSWR from 'swr'
import fetcher from '@lib/fetcher'

const OpeningTimes = ({ date }) => {
  const [showFullTimes, setShowFullTimes] = useState(false)
  const [currentTime, setCurrentTime] = useState(new Date())

  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date())
    }, 60000) // Update every minute

    return () => clearInterval(timer)
  }, [])

  // Fetch opening times for the current date
  const { data: todayData, error: todayError } = useSWR(
    `/opening_times/${date || 'today'}`,
    fetcher,
    {
      refreshInterval: 300000 // Refresh every 5 minutes
    }
  )


  if (todayError) {
    console.error('Error fetching opening times:', todayError)
    return null
  }

  if (!todayData) {
    return null
  }
  
  // If there's no meaningful opening times data, don't show anything
  // Check if closed is explicitly false or if we have opening times
  const hasOpeningInfo = todayData.opens_at || todayData.closes_at || todayData.closed === true
  
  // Check if summary has actual location/time data (not just ["Closed"])
  const hasValidSummary = todayData.summary && 
    Array.isArray(todayData.summary) && 
    todayData.summary.length > 0 &&
    todayData.summary.some(item => 
      Array.isArray(item) && 
      item.length >= 2 && 
      item[0] !== 'Closed'
    )
  
  if (!hasOpeningInfo && !hasValidSummary) {
    return null
  }

  const formatTimeDisplay = () => {
    // Get the displayed date and format it
    const displayedDate = date || new Date().toISOString().split('T')[0]
    const dateObj = new Date(displayedDate + 'T12:00:00') // Use noon to avoid timezone issues
    const today = new Date().toISOString().split('T')[0]
    const isToday = displayedDate === today
    
    if (todayData.closed) {
      if (todayData.next_opening) {
        return `Closed ${isToday ? 'today' : dateObj.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short' })}, will open ${todayData.next_opening.day_name} at ${todayData.next_opening.opens_at}`
      }
      return `Closed ${isToday ? 'today' : dateObj.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short' })}`
    }

    // If not today, just show the opening hours
    if (!isToday) {
      return `Open ${todayData.opens_at} - ${todayData.closes_at}`
    }

    // For today, check the current time status
    const now = currentTime
    const currentHours = now.getHours()
    const currentMinutes = now.getMinutes()
    const currentTimeInMinutes = currentHours * 60 + currentMinutes

    const [openHours, openMinutes] = todayData.opens_at.split(':').map(Number)
    const [closeHours, closeMinutes] = todayData.closes_at
      .split(':')
      .map(Number)
    const openTimeInMinutes = openHours * 60 + openMinutes
    const closeTimeInMinutes = closeHours * 60 + closeMinutes

    // Check current status for today only
    if (currentTimeInMinutes < openTimeInMinutes) {
      // Before opening time
      return `Open ${todayData.opens_at} - ${todayData.closes_at}`
    } else if (currentTimeInMinutes >= closeTimeInMinutes) {
      // After closing time - show next opening
      if (todayData.next_opening) {
        return `Closed, will re-open ${todayData.next_opening.day_name} at ${todayData.next_opening.opens_at}`
      }
      return 'Closed'
    } else {
      // Currently open (only for today)
      return `Open now, closes ${todayData.closes_at}`
    }
  }

  // Check if there are any opening times to show (reuse hasValidSummary)
  const hasOpeningTimes = hasValidSummary

  return (
    <div className='bg-gray-100 dark:bg-gray-800 rounded-lg p-3 mb-2'>
      <div
        className={`flex justify-between items-center ${hasOpeningTimes ? 'cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700' : ''} -m-1 p-1 rounded transition-colors`}
        onClick={hasOpeningTimes ? () => setShowFullTimes(!showFullTimes) : undefined}
      >
        <div className='flex items-center gap-2'>
          <svg
            className='w-4 h-4 text-gray-600 dark:text-gray-400'
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
            />
          </svg>
          <span className='text-gray-700 dark:text-gray-200'>{formatTimeDisplay()}</span>
        </div>
        {hasOpeningTimes && (
          <svg
            className={`w-4 h-4 text-gray-600 dark:text-gray-400 transition-transform ${
              showFullTimes ? 'rotate-180' : ''
            }`}
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M19 9l-7 7-7-7'
            />
          </svg>
        )}
      </div>

      {showFullTimes && todayData.summary && Array.isArray(todayData.summary) && todayData.summary.length > 0 && (
        <div className='mt-3 border-t border-gray-300 dark:border-gray-700 pt-3'>
          <h3 className='text-gray-700 dark:text-gray-300 mb-2 text-xs uppercase tracking-wider'>
            Opening times on {new Date(todayData.date).toLocaleDateString('en-GB', { 
              weekday: 'long', 
              day: 'numeric',
              month: 'long'
            })}
          </h3>
          <div className='space-y-1'>
            {todayData.summary.map((location, index) => {
              // Ensure location is an array with at least 2 elements
              if (!Array.isArray(location) || location.length < 2) {
                return null
              }
              
              const locationName = location[0] || 'Unknown'
              const times = Array.isArray(location[1]) ? location[1] : [location[1] || 'Closed']
              
              return (
                <div
                  key={index}
                  className='flex justify-between text-xs text-gray-600 dark:text-gray-400'
                >
                  <span>{locationName}</span>
                  <span>{times.join(', ')}</span>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

export default OpeningTimes
